import { supabase } from '@/integrations/supabase/client';
import { BaseService } from './BaseService';

export type MBTIDimensionKey = 'EI' | 'SN' | 'TF' | 'JP';
export type FrameworkType = 'mbti' | 'bigfive' | 'enneagram' | 'alignment' | 'holland' | 'socionics' | 'integral' | 'attachment';

export interface FrameworkWeights {
  [key: string]: {
    traits: Record<string, number>;
    threshold?: number;
    scaling?: number;
  };
}

export interface ScoringOverrides {
  traitMappings?: Record<string, number[]>; // trait -> question indices (1-based)
  mbti?: Record<MBTIDimensionKey, { traits: Record<string, number>; threshold?: number }>;
  bigfive?: FrameworkWeights;
  enneagram?: FrameworkWeights;
  alignment?: FrameworkWeights;
  holland?: FrameworkWeights;
  socionics?: FrameworkWeights;
  integral?: FrameworkWeights;
  attachment?: FrameworkWeights;
}

export interface UserOverride {
  userId: string;
  framework: FrameworkType;
  overrides: Partial<ScoringOverrides>;
  reason?: string;
}

const LOCAL_STORAGE_KEY = 'tps_scoring_overrides';

/**
 * Scoring configuration service for managing framework weights and user overrides.
 * Extends BaseService for consistent error handling and retry logic.
 */
class ScoringConfigServiceImpl extends BaseService {
  constructor() {
    super('ScoringConfigService');
  }

  /**
   * Load global scoring overrides from llm_config table
   */
  async loadScoringOverrides(): Promise<ScoringOverrides | null> {
    return this.execute(async () => {
      const { data, error } = await supabase
        .from('llm_config')
        .select('config, mapping_weights')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      const mappingWeights = (data?.mapping_weights || {}) as any;
      const config = (data?.config || {}) as any;

      const overrides: ScoringOverrides = {
        traitMappings: config?.scoring_overrides?.trait_mappings || mappingWeights?.trait_mappings || undefined,
        mbti: mappingWeights?.mbti || config?.scoring_overrides?.mbti || undefined,
        bigfive: config?.scoring_overrides?.bigfive || mappingWeights?.bigfive || undefined,
        enneagram: config?.scoring_overrides?.enneagram || mappingWeights?.enneagram || undefined,
        alignment: config?.scoring_overrides?.alignment || mappingWeights?.alignment || undefined,
        holland: config?.scoring_overrides?.holland || mappingWeights?.holland || undefined,
        socionics: config?.scoring_overrides?.socionics || mappingWeights?.socionics || undefined,
        integral: config?.scoring_overrides?.integral || mappingWeights?.integral || undefined,
        attachment: config?.scoring_overrides?.attachment || mappingWeights?.attachment || undefined,
      };

      return Object.values(overrides).some(v => v !== undefined) ? overrides : null;
    }, { action: 'loadScoringOverrides' });
  }

  /**
   * Save global scoring overrides (merges with existing)
   */
  async saveScoringOverrides(partial: ScoringOverrides): Promise<void> {
    return this.execute(async () => {
      // Read latest existing to preserve other fields
      const { data: existing } = await supabase
        .from('llm_config')
        .select('config, mapping_weights')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const currentConfig = (existing?.config || {}) as any;
      const currentWeights = (existing?.mapping_weights || {}) as any;

      const nextConfig = {
        ...currentConfig,
        scoring_overrides: {
          ...(currentConfig?.scoring_overrides || {}),
          trait_mappings: partial.traitMappings ?? currentConfig?.scoring_overrides?.trait_mappings,
          mbti: partial.mbti ?? currentConfig?.scoring_overrides?.mbti,
          bigfive: partial.bigfive ?? currentConfig?.scoring_overrides?.bigfive,
          enneagram: partial.enneagram ?? currentConfig?.scoring_overrides?.enneagram,
          alignment: partial.alignment ?? currentConfig?.scoring_overrides?.alignment,
          holland: partial.holland ?? currentConfig?.scoring_overrides?.holland,
          socionics: partial.socionics ?? currentConfig?.scoring_overrides?.socionics,
          integral: partial.integral ?? currentConfig?.scoring_overrides?.integral,
          attachment: partial.attachment ?? currentConfig?.scoring_overrides?.attachment,
        }
      };

      const nextMappingWeights = {
        ...currentWeights,
        trait_mappings: partial.traitMappings ?? currentWeights?.trait_mappings,
        mbti: partial.mbti ?? currentWeights?.mbti,
        bigfive: partial.bigfive ?? currentWeights?.bigfive,
        enneagram: partial.enneagram ?? currentWeights?.enneagram,
        alignment: partial.alignment ?? currentWeights?.alignment,
        holland: partial.holland ?? currentWeights?.holland,
        socionics: partial.socionics ?? currentWeights?.socionics,
        integral: partial.integral ?? currentWeights?.integral,
        attachment: partial.attachment ?? currentWeights?.attachment,
      };

      const { error } = await supabase.from('llm_config').insert({
        config: nextConfig,
        mapping_weights: nextMappingWeights,
      });

      if (error) throw error;
    }, { action: 'saveScoringOverrides' });
  }

  /**
   * Save user-specific override for a framework
   */
  async saveUserOverride(
    userId: string, 
    framework: FrameworkType, 
    overrides: Partial<ScoringOverrides>, 
    reason?: string
  ): Promise<void> {
    this.validateRequired({ userId, framework }, ['userId', 'framework']);

    return this.execute(async () => {
      const user = await this.getCurrentUser();
      
      const { error } = await supabase
        .from('personality_overrides')
        .upsert({
          user_id: userId,
          created_by: user.id,
          [`${framework}_override`]: overrides[framework],
          metadata: { reason, framework, timestamp: new Date().toISOString() }
        } as any, { onConflict: 'user_id' });

      if (error) throw error;
    }, { action: 'saveUserOverride', userId, framework });
  }

  /**
   * Load user-specific overrides
   */
  async loadUserOverride(userId: string): Promise<any | null> {
    return this.execute(async () => {
      const { data, error } = await supabase
        .from('personality_overrides')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        // PGRST116 = no rows returned, not an error for this case
        if (error.code !== 'PGRST116') throw error;
        return null;
      }
      return data;
    }, { action: 'loadUserOverride', userId });
  }

  /**
   * Write overrides to local storage (sync operation)
   */
  writeLocalOverrides(overrides: ScoringOverrides | null): void {
    if (!overrides) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return;
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(overrides));
  }

  /**
   * Read overrides from local storage (sync operation)
   */
  readLocalOverrides(): ScoringOverrides | null {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const scoringConfigService = new ScoringConfigServiceImpl();

// Legacy function exports for backwards compatibility
export const loadScoringOverrides = () => scoringConfigService.loadScoringOverrides();
export const saveScoringOverrides = (partial: ScoringOverrides) => scoringConfigService.saveScoringOverrides(partial);
export const saveUserOverride = (
  userId: string, 
  framework: FrameworkType, 
  overrides: Partial<ScoringOverrides>, 
  reason?: string
) => scoringConfigService.saveUserOverride(userId, framework, overrides, reason);
export const loadUserOverride = (userId: string) => scoringConfigService.loadUserOverride(userId);
export const writeLocalOverrides = (overrides: ScoringOverrides | null) => scoringConfigService.writeLocalOverrides(overrides);
export const readLocalOverrides = () => scoringConfigService.readLocalOverrides();
